import type { CoreEnv, CoreRequest } from '@eventbrite/context-gen';
import { DiscoveryEvent } from '@eventbrite/event-renderer';
import {
    getLocaleOverride,
    gettext,
    setLanguage,
    setup as setupI18n,
} from '@eventbrite/i18n';
import { SiteStructure } from '@eventbrite/site-structure';
import * as Sentry from '@sentry/react';
import classNames from 'classnames';
import React from 'react';
import { renderToString } from 'react-dom/server';
import { App } from './app';
import { CreatorBanner } from './components/CreatorBanner/CreatorBanner';
import { EventBody } from './components/EventBody/EventBody';
import { ActionsPanel } from './components/EventDetails/ActionsPanel/ActionsPanel';
import { ConversionBar } from './components/EventDetails/ConversionBar/ConversionBar';
import { CreatorCollections } from './components/EventDetails/CreatorCollections/CreatorCollections';
import {
    EventDetails,
    EventDetailsAside,
    EventDetailsMain,
    EventDetailsMainInner,
    EventDetailsWrapper,
} from './components/EventDetails/EventDetails';
import { HeroImage } from './components/EventDetails/HeroImage/HeroImage';
import { MusicEventsInfo } from './components/EventDetails/MusicEventsInfo/MusicEventsInfo';
import { OrganizerInfo } from './components/EventDetails/OrganizerInfo/OrganizerInfo';
import { Performers } from './components/EventDetails/Performers/Performers';
import { RefundPolicy } from './components/EventDetails/RefundPolicy/RefundPolicy';
import { SalesEndedMessage } from './components/EventDetails/SalesEndedMessage/SalesEndedMessage';
import { StartDate } from './components/EventDetails/StartDate/StartDate';
import { Summary } from './components/EventDetails/Summary/Summary';
import { WhenAndWhere } from './components/EventDetails/WhenAndWhere/WhenAndWhere';
import type { EventHeroComponentProps } from './components/EventHero/EventHero';
import { EventHero } from './components/EventHero/EventHero';
import type { EventDescriptionProps } from './components/EventInfo/EventDescription/EventDescription';
import {
    AboutThisEvent,
    OldDescriptionBeforeStructuredContent,
} from './components/EventInfo/EventDescription/EventDescription';
import { EventInfo } from './components/EventInfo/EventInfo';
import { EventMapLayout } from './components/EventMap/EventMapLayout';
import { ExpiredEventsPage } from './components/ExpiredEvents/ExpiredEventsPage';
import type { FooterLinks } from './components/Footer/Footer';
import { Footer } from './components/Footer/Footer';
import type { LegacyConversionBarProps } from './components/LegacyConversionBar/LegacyConversionBar';
import { LegacyConversionBar } from './components/LegacyConversionBar/LegacyConversionBar';
import { MainBodyBackground } from './components/MainBodyBackground/MainBodyBackground';
import type { NotificationBarSsrProps } from './components/NotificationBar/NotificationBarSsr';
import { NotificationBarSsr } from './components/NotificationBar/NotificationBarSsr';
import type { OrganizerPanelLayoutProps } from './components/OrganizerPanel/OrganizerPanelLayout';
import { OrganizerPanelLayout } from './components/OrganizerPanel/OrganizerPanelLayout';
import {
    getShareComponentProps,
    ShareProps,
} from './components/ShareEvent/ShareEventRoot';
import type { ShareInlineProps } from './components/ShareInline/ShareInline';
import { ShareInline } from './components/ShareInline/ShareInline';
import type { Tag } from './components/Tags/Tags';
import { Tags } from './components/Tags/Tags';
import {
    UrgencySignalsProps,
    UrgencySignalsProvider,
} from './components/UrgencySignals';
import { EVENT_DETAILS_FEATURE_GATE } from './constants';
import { checkGateFromServer } from './statsig';
import type {
    AppProps,
    Event,
    EventDetailsProps,
    EventMapScopedProps,
    MainBodyBackgroundScopedProps,
    Organizer,
    Settings,
    StatsigProps,
    User,
} from './types';

const FEATURE_FLAGS = {
    launchCreatorCreditsSectionAttendee: true,
};

export interface ServerAppProps {
    conversionBar: LegacyConversionBarProps;
    user: User;
    event: Event;
    env: CoreEnv;
    request: CoreRequest;
    footerLinks: FooterLinks;
    eventDescription: EventDescriptionProps;
    eventDetails: EventDetailsProps;
    organizer: Organizer;
    tags: Array<Tag>;
    settings: Settings;
    shareInline: ShareInlineProps;
    organizerPanelLayout: OrganizerPanelLayoutProps;
    eventMapLayout: EventMapScopedProps;
    eventHero: EventHeroComponentProps;
    mainBodyBackground: MainBodyBackgroundScopedProps;
    notificationBar: NotificationBarSsrProps;
    expiredEventsPage: {
        shareComponentProps: ShareProps;
        relatedEvents: DiscoveryEvent[];
    };
    statsig: StatsigProps;
    urgencySignals: UrgencySignalsProps;
}

export const ServerApp = ({
    conversionBar,
    user,
    event,
    env,
    request,
    footerLinks,
    eventDescription,
    eventDetails,
    organizer,
    tags,
    settings,
    shareInline,
    organizerPanelLayout,
    eventMapLayout,
    eventHero,
    notificationBar = {
        message: null,
        type: null,
    },
    mainBodyBackground,
    expiredEventsPage,
    statsig,
    urgencySignals,
}: ServerAppProps) => {
    const { hasCroppedLogo } = event;

    const {
        shouldShowSalesEndedMessage,
        organizerSalesEndedMessage,
        salesEndedMessage,
    } = event;

    const {
        hasStructuredContent,
        structuredContent,
        summary,
        legacyDescription,
    } = eventDescription;

    const { performers } = eventDetails;
    const hasPerformers = performers !== null;

    const isRepeatingEvent =
        event.isSeriesParent ||
        (event.childEvents && event.childEvents.length > 0);

    const isEventDetailsFeatureGateActive =
        settings.featureFlags?.enable_event_details_on_listings &&
        checkGateFromServer(EVENT_DETAILS_FEATURE_GATE, statsig);

    const shouldShowRicherEventDetails =
        isEventDetailsFeatureGateActive && !isRepeatingEvent;

    const EventBodyComponent = () => (
        <EventBody>
            <UrgencySignalsProvider urgencySignals={urgencySignals}>
                <EventHero
                    {...{
                        ...eventHero,
                        hasCroppedLogo: event.hasCroppedLogo,
                        isEnded: event.isEnded,
                        isSeriesParent: event.isSeriesParent,
                        hideStartDate: event.hideStartDate,
                        startDateTime: event.startDateTime,
                        startMonthAbbr: event.startMonthAbbr,
                        startDay: event.startDay,
                        hasDonationTicketsAvailable:
                            event.hasDonationTicketsAvailable,
                        eventName: event.name,
                        displayOrganizationName:
                            organizer.displayOrganizationName,
                        organizationNameTruncated: organizer.nameTruncated,
                        orgProfileLink: organizer.url,
                        shouldDisplayRsvp: event.shouldDisplayRsvp,
                    }}
                />
                <LegacyConversionBar
                    {...{
                        ...conversionBar,
                        externalTickets: {
                            url: conversionBar.externalTicketsUrl,
                            text: conversionBar.externalTicketsButtonText,
                            available:
                                conversionBar.externalTicketsAreAvailable,
                        },
                        id: event.id,
                        childEvents: event.childEvents,
                        isSeriesParent: event.isSeriesParent,
                    }}
                />
            </UrgencySignalsProvider>

            <EventInfo
                {...{
                    description: { ...eventDescription },
                    tags,
                    shareInline: { ...shareInline },
                    eventDetails: {
                        ...{
                            ...eventDetails,
                            location: {
                                ...eventDetails.location,
                                isOnlineEvent: event.isOnlineEvent,
                                onlineEventUrl: eventDetails.onlineEvent?.url,
                            },
                            dates: {
                                ...eventDetails.dates,
                                isSeriesParent: event.isSeriesParent,
                                childEvents: event.childEvents || [],
                                hideEndDate: event.hideEndDate,
                            },
                        },
                    },
                }}
            />

            <EventMapLayout {...eventMapLayout} />
            <OrganizerPanelLayout {...organizerPanelLayout} />
        </EventBody>
    );

    const MainContent = () => (
        <>
            <div
                className={`js-hidden-when-expired event-listing ${
                    hasCroppedLogo ? 'event-listing--has-image' : ''
                } ${shouldHideMainBody ? 'is-hidden' : ''}`}
                role="article"
            >
                {notificationBar.message && (
                    <NotificationBarSsr {...notificationBar} />
                )}

                {shouldShowRicherEventDetails ? (
                    <>
                        {user.canManageEvent &&
                            !settings.featureFlags
                                ?.launchMobilePreviewInEventCreationFlow && (
                                <CreatorBanner
                                    eventId={event.id}
                                    settings={settings}
                                />
                            )}
                        <EventDetails>
                            <HeroImage
                                background={mainBodyBackground.croppedLogoUrl50}
                                hasCroppedLogo={event.hasCroppedLogo}
                                croppedLogoUrl480={eventHero.croppedLogoUrl480}
                                croppedLogoUrl600={eventHero.croppedLogoUrl600}
                                croppedLogoUrl800={eventHero.croppedLogoUrl800}
                                croppedLogoUrl940={eventHero.croppedLogoUrl940}
                                eventName={event.name}
                            />
                            <EventDetailsWrapper>
                                <ActionsPanel
                                    isShareable={conversionBar.isShareable}
                                    isEnded={conversionBar.isEnded}
                                />
                                <EventDetailsMain>
                                    <EventDetailsMainInner>
                                        <SalesEndedMessage
                                            shouldShowSalesEndedMessage={
                                                shouldShowSalesEndedMessage
                                            }
                                            organizerSalesEndedMessage={
                                                organizerSalesEndedMessage
                                            }
                                            salesEndedMessage={
                                                salesEndedMessage
                                            }
                                        />
                                        <StartDate
                                            startDateTime={event.startDateTime}
                                            startMonthAbbr={
                                                event.startMonthAbbr
                                            }
                                            startDay={event.startDay}
                                        />
                                        <meta content={event.name} />
                                        <h1 className="event-title">
                                            {event.name}
                                        </h1>
                                        {eventHero.hasPresenterOrAgeRestriction ? (
                                            <MusicEventsInfo
                                                ageRestriction={
                                                    eventHero.ageRestriction
                                                }
                                                presenter={eventHero.presenter}
                                            />
                                        ) : null}
                                        <CreatorCollections
                                            eventId={event.id}
                                        />
                                        <Summary summary={summary} />
                                        {organizer.displayOrganizationName && (
                                            <OrganizerInfo
                                                profileLink={organizer.url}
                                                nameTruncated={
                                                    organizer.nameTruncated
                                                }
                                            />
                                        )}
                                    </EventDetailsMainInner>
                                    <WhenAndWhere
                                        dates={{
                                            ...eventDetails.dates,
                                            hideEndDate: event.hideEndDate,
                                            hideStartDate: event.hideStartDate,
                                        }}
                                        location={{
                                            ...eventDetails.location,
                                            isOnlineEvent: event.isOnlineEvent,
                                            onlineEventUrl:
                                                eventDetails.onlineEvent?.url,
                                        }}
                                        map={{
                                            location: eventMapLayout.location,
                                            gMapsApiKey:
                                                eventMapLayout.gMapsApiKey,
                                            shouldDisplayMap:
                                                eventMapLayout.shouldDisplayMap,
                                        }}
                                    />
                                    <EventDetailsMainInner>
                                        {hasPerformers && (
                                            <Performers
                                                headliners={
                                                    eventDetails.performers
                                                        .headliners
                                                }
                                                supporters={
                                                    eventDetails.performers
                                                        .supporters
                                                }
                                            />
                                        )}
                                        <RefundPolicy
                                            isEnableRetainFeeActive={
                                                eventDetails.refund
                                                    .isEnableRetainFeeActive
                                            }
                                            isPaidEvent={
                                                eventDetails.refund.isPaidEvent
                                            }
                                            isRefundRequestAllowed={
                                                eventDetails.refund
                                                    .isRefundRequestAllowed
                                            }
                                            refundPolicyDescription={
                                                eventDetails.refund
                                                    .refundPolicyDescription
                                            }
                                            refundPolicyIsNoRefunds={
                                                eventDetails.refund
                                                    .refundPolicyIsNoRefunds
                                            }
                                        />
                                        {hasStructuredContent ||
                                        !legacyDescription ? (
                                            <AboutThisEvent
                                                hasStructuredContent={
                                                    hasStructuredContent
                                                }
                                                admat={structuredContent.admat}
                                                modules={
                                                    structuredContent.modules
                                                }
                                                shouldShowRicherEventDetails={
                                                    shouldShowRicherEventDetails
                                                }
                                            />
                                        ) : (
                                            <OldDescriptionBeforeStructuredContent
                                                legacyDescription={
                                                    legacyDescription
                                                }
                                                shouldShowRicherEventDetails={
                                                    shouldShowRicherEventDetails
                                                }
                                            />
                                        )}
                                        <Tags
                                            tags={tags}
                                            shouldShowRicherEventDetails={
                                                shouldShowRicherEventDetails
                                            }
                                        />
                                        <ShareInline
                                            {...{
                                                ...shareInline,
                                                shouldShowRicherEventDetails:
                                                    shouldShowRicherEventDetails,
                                            }}
                                        />
                                    </EventDetailsMainInner>
                                </EventDetailsMain>
                                <EventDetailsAside>
                                    <ConversionBar
                                        {...{
                                            ...conversionBar,
                                            externalTickets: {
                                                url: conversionBar.externalTicketsUrl,
                                                text: conversionBar.externalTicketsButtonText,
                                                available:
                                                    conversionBar.externalTicketsAreAvailable,
                                                shouldShowExternalTicketingProvider:
                                                    eventHero.shouldShowExternalTicketingProvider,
                                                externalTicketingProviderLabelText:
                                                    eventHero.externalTicketingProviderLabelText,
                                            },
                                            id: event.id,
                                            childEvents: event.childEvents,
                                            isSeriesParent:
                                                event.isSeriesParent,
                                            isExpiredEvent:
                                                event.isExpiredEvent,
                                        }}
                                    />
                                </EventDetailsAside>
                            </EventDetailsWrapper>
                        </EventDetails>
                        <div className="event-listing__body old-non-event-details">
                            <OrganizerPanelLayout {...organizerPanelLayout} />
                        </div>
                    </>
                ) : (
                    <>
                        <MainBodyBackground
                            hasCroppedLogo={hasCroppedLogo}
                            croppedLogoUrl50={
                                mainBodyBackground.croppedLogoUrl50
                            }
                        />

                        <div className="g-grid g-grid--page-margin-manual">
                            {user.canManageEvent &&
                                !settings.featureFlags
                                    ?.launchMobilePreviewInEventCreationFlow && (
                                    <CreatorBanner
                                        eventId={event.id}
                                        settings={settings}
                                    />
                                )}
                            <EventBodyComponent />
                        </div>
                    </>
                )}
                {shouldShowRelatedEventsLive && (
                    <div id="listings-root__related-events"></div>
                )}
                {shouldShowBoostBanner && (
                    <div id="listings-root__boost-banner-preview"></div>
                )}
            </div>

            {shouldHideMainBody && (
                <ExpiredEventsPage
                    relatedEvents={expiredEventsPage.relatedEvents}
                    eventName={event.name}
                    organizer={{
                        name: organizer.name,
                        profilePicture: organizer.profilePicture,
                        url: organizer.url,
                    }}
                    isAuthenticated={user.isAuthenticated}
                    shareComponentProps={expiredEventsPage.shareComponentProps}
                />
            )}
        </>
    );
    const shouldHideMainBody =
        !event.isPreview && event.shouldShowRelatedEventsExpired;

    const shouldShowRelatedEventsLive = event.isLive;

    const shouldShowBoostBanner =
        settings.featureFlags?.enable_boost_banner_on_listings &&
        user.canManageEvent;

    const cleanEsiCachePaths = (env: CoreEnv) => {
        const esiPath = user.isAuthenticated
            ? /%2Fesi_nocache/g
            : /%2Fesi_cache/g;
        const removeEsiPath = (string: string): string =>
            string ? string.replace(esiPath, '') : '';

        env.loginUrl = removeEsiPath(env.loginUrl);
        env.logoutUrl = removeEsiPath(env.logoutUrl);
        env.signinUrl = removeEsiPath(env.signinUrl);
        env.signupUrl = removeEsiPath(env.signupUrl);
    };

    cleanEsiCachePaths(env);

    const siteWrapperClasses = classNames({
        'conversion-bar-placeholder': !event.isExpiredEvent,
    });

    return (
        <Sentry.ErrorBoundary
            fallback={<>{gettext('Error on Listings SSR')}</>}
        >
            <div className={siteWrapperClasses}>
                <SiteStructure
                    env={env}
                    request={request}
                    user={user}
                    preview={event.isPreview}
                    featureFlags={FEATURE_FLAGS}
                    fullScreenMain={true}
                    footer={
                        <Footer {...footerLinks} serverUrl={env.serverUrl} />
                    }
                    removeOverflowSettings={true}
                    focusDrawerOptions={{ content: '', alwaysInclude: false }}
                >
                    <MainContent />
                </SiteStructure>
            </div>
        </Sentry.ErrorBoundary>
    );
};

export const render = (props: AppProps) => {
    const locale = getLocaleOverride() || props.env.localeInfo.locale;
    setupI18n(require.context('./i18n/translations', true, /\.json$/));
    setLanguage(locale);

    return renderToString(
        <ServerApp
            user={props.user}
            event={{ ...props.event }}
            request={props.request}
            env={props.env}
            footerLinks={props.footerLinks}
            eventDescription={{ ...props.components.eventDescription }}
            eventDetails={{ ...props.components.eventDetails }}
            tags={props.components.tags}
            shareInline={props.components.shareInline}
            conversionBar={props.components.conversionBar}
            settings={props.settings}
            organizerPanelLayout={props.components.organizerPanel}
            eventMapLayout={props.components.eventMap}
            eventHero={props.components.eventHero}
            organizer={props.organizer}
            mainBodyBackground={props.components.mainBodyBackground}
            notificationBar={props.components.notificationBar}
            expiredEventsPage={{
                ...props.components.relatedEventsExpiredPage,
                shareComponentProps: getShareComponentProps(props),
            }}
            statsig={props.statsig}
            urgencySignals={props.urgencySignals}
        />,
    );
};

export default App;
